import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { ReactComponent as CloseIcon } from '../../shared/icons/close-icon.svg';
import { ReactComponent as LockIcon } from '../../shared/icons/lock-icon.svg';
import { ReactComponent as HistoryIcon } from '../../shared/icons/clock-icon.svg';
import { ReactComponent as BackIcon } from '../../shared/icons/chevron-left.svg';
import { ReactComponent as PlusIcon } from '../../shared/icons/plus-icon.svg';
import { ReactComponent as VolumeIcon } from '../../shared/icons/volume-icon.svg';
import { ReactComponent as VolumeOffIcon } from '../../shared/icons/volume-off-icon.svg';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { ChatView } from './components/ChatView';
import { ChatHistory } from './components/ChatHistory';
import { ChatScopeSelector, ChatScope } from './components/ChatScopeSelector';
import { DealPicker } from './components/DealPicker';
import { DocumentPicker } from './components/DocumentPicker';
import { useAudioPlayback } from './hooks/useAudioPlayback';
import { useIsMobile } from '../../shared/hooks/useIsMobile';
import { A } from '../../routing';
import { useUserRestrictions } from '../../auth/user-restrictions';
import { useTeamInfo } from '../../team-id-context';
import { ASK_DOCUMENT } from '../../api/mutations/askDocument';
import { DELETE_CHAT_SESSION } from '../../api/mutations/chatSessions';
import { GET_MY_CHAT_SESSIONS, GET_CHAT_SESSION_BY_TOKEN } from '../../api/queries/chatSessions';
import { useNotifications } from '../../notifications';
import styles from './AiChatSlideout.module.scss';

type ViewType = 'history' | 'chat';

export interface ChatSession {
  id: string;
  title: string;
  documentId?: string;
  documentName?: string;
  dealId?: string;
  dealName?: string;
  scope?: string;
  lastMessage?: string;
  messageCount: number;
  createdAt: Date;
  updatedAt: Date;
  sessionToken?: string;
}

interface ChatSource {
  documentId?: string;
  documentTitle?: string;
  pageNumber?: number;
  content?: string;
}

export interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  sources?: ChatSource[];
}

interface AiChatSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  documentId?: string;
  documentName?: string;
  teamId?: string;
  dealId?: string;
}

export const AiChatSlideout: React.FC<AiChatSlideoutProps> = ({
  isOpen,
  onClose,
  documentId,
  documentName,
  teamId: _teamId,
  dealId,
}) => {
  const { teamId: contextTeamId } = useTeamInfo();
  const teamId = _teamId || contextTeamId;

  const [showTooltip, setShowTooltip] = useState(false);
  const [currentView, setCurrentView] = useState<ViewType>('chat');
  const [chatScope, setChatScope] = useState<ChatScope | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | undefined>(documentId);
  const [selectedDocumentName, setSelectedDocumentName] = useState<string | undefined>(documentName);
  const [selectedDealId, setSelectedDealId] = useState<string | undefined>();
  const [selectedDealName, setSelectedDealName] = useState<string | undefined>();
  const pageDealId = dealId; // Keep track of the page's dealId separately
  // Initialize with welcome message if document is pre-selected
  const [messages, setMessages] = useState<Message[]>(() => {
    if (documentId && documentName) {
      return [{
        id: `initial-${Date.now()}`,
        content: `Ask me any question about "${documentName}"`,
        role: 'assistant',
        timestamp: new Date()
      }];
    }
    return [];
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingSession, setIsDeletingSession] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(() => {
    return localStorage.getItem('ai-chat-autoplay') === 'true';
  });
  const scrollRef = React.useRef<HTMLDivElement>(null);
  // Which document the conversation was last seeded for.
  const seededDocumentRef = React.useRef<string | undefined>(documentId);
  const { creatableTemplateTypes, restrictionsLoading } = useUserRestrictions();
  const client = useApolloClient();
  const [, { error: notifyError, success: notifySuccess }] = useNotifications();
  const isMobile = useIsMobile();

  // Fetch chat sessions
  const { data: sessionsData, loading: _sessionsLoading, refetch: refetchSessions } = useQuery(GET_MY_CHAT_SESSIONS, {
    variables: { limit: 20 },
    skip: !isOpen,
  });

  // Audio playback hook
  const { play: playAudio, stop: stopAudio, isPlaying } = useAudioPlayback({
    autoPlay: true,
    onPlaybackEnd: () => {
      // Optional: Handle playback end
    },
  });
  
  // Handle pre-selected document from props.
  //
  // The greeting cannot come from the `messages` useState initializer: this slideout
  // is mounted for the lifetime of the header, so that initializer ran once at app
  // start with no document and never runs again. Opening it for a document has to
  // seed the conversation here instead.
  useEffect(() => {
    if (!documentId) {
      // Cleared on close, so the next hand-off seeds a fresh greeting.
      seededDocumentRef.current = undefined;
      return;
    }
    if (!documentName || !isOpen) return;

    setChatScope('document');
    setSelectedDocumentId(documentId);
    setSelectedDocumentName(documentName);

    // Only start over for a DIFFERENT document. Reopening the panel on the one
    // already being discussed must not discard that conversation -- and carrying a
    // previous document's session token into a new document would let the assistant
    // answer about the wrong one.
    if (seededDocumentRef.current === documentId) return;
    seededDocumentRef.current = documentId;

    setSessionToken(null);
    setInput('');
    setCurrentView('chat');
    setMessages([{
      id: `initial-${Date.now()}`,
      content: `Ask me anything about "${documentName}"`,
      role: 'assistant',
      timestamp: new Date()
    }]);
  }, [documentId, documentName, isOpen]);

  // Add/remove body class for document push effect on desktop
  useEffect(() => {
    if (isOpen && !isMobile) {
      document.body.classList.add('ai-chat-open');
    } else {
      document.body.classList.remove('ai-chat-open');
    }

    return () => {
      document.body.classList.remove('ai-chat-open');
    };
  }, [isOpen, isMobile]);
  
  // Simple click outside handler for tooltip
  useEffect(() => {
    if (showTooltip) {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        // Close if clicked outside button or tooltip
        if (!target.closest('#ai-assistant-info-btn') && !target.closest('[role="tooltip"]')) {
          setShowTooltip(false);
        }
      };
      
      // Small delay to prevent immediate close on the same click that opened it
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 10);
      
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showTooltip]);
  
  const [askDocument] = useMutation(ASK_DOCUMENT, {
    onError: (error) => {
      notifyError({ message: error.message || 'Failed to get response from AI' });
      setIsLoading(false);
    }
  });
  
  const [deleteChatSession] = useMutation(DELETE_CHAT_SESSION, {
    onCompleted: () => {
      notifySuccess({ message: 'Chat deleted successfully' });
      refetchSessions();
      setIsDeletingSession(false);
    },
    onError: (error) => {
      notifyError({ message: error.message || 'Failed to delete chat' });
      setIsDeletingSession(false);
    }
  });

  // Auto-scroll to show new messages properly (top of message visible)
  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        const container = scrollRef.current;
        if (!container) return;
        
        // Get all message elements
        const messageElements = container.querySelectorAll('[class*="message"]');
        if (messageElements.length > 0) {
          // Get the last message element
          const lastMessage = messageElements[messageElements.length - 1] as HTMLElement;
          
          // Calculate scroll position to show the top of the last message
          const messageTop = lastMessage.offsetTop;
          const containerHeight = container.clientHeight;
          const messageHeight = lastMessage.offsetHeight;
          
          // If message fits in view, scroll to show its top with some padding
          // Otherwise, scroll to the top of the message
          const scrollPosition = messageHeight > containerHeight 
            ? messageTop - 20  // Just show top of long message with padding
            : Math.max(0, messageTop - containerHeight + messageHeight + 40); // Show full message with bottom padding
          
          container.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [messages, isLoading]);

  // Remove this check - we need to keep component rendered for transitions to work
  // The visibility will be controlled by CSS classes instead

  // Don't show anything while loading restrictions to prevent flash
  if (restrictionsLoading) {
    return ReactDOM.createPortal(
      <>
        {/* Dark overlay backdrop - only show on mobile */}
        {isOpen && isMobile && <div className={styles.slideoutBackdrop} />}
        <div className={classNames(
          styles.slideoutWrapper, 
          isOpen ? styles.slideoutWrapper__show : styles.slideoutWrapper__collapsed
        )}>
          <div className={styles.slideout}>
            <div className={styles.slideout__header}>
              <div className={styles.slideout__title}>AI Assistant</div>
              <div className={styles.slideout__close} onClick={onClose}>
                <CloseIcon />
              </div>
            </div>
            <div className={styles.slideout__content}>
              {/* Empty during loading to prevent flash */}
            </div>
          </div>
        </div>
      </>,
      document.body
    );
  }

  // Check if user has premium subscription (can create templates)
  const canUseAiChat = creatableTemplateTypes && creatableTemplateTypes.length > 0;
  
  if (!canUseAiChat) {
    return ReactDOM.createPortal(
      <>
        {/* Dark overlay backdrop - only show on mobile */}
        {isOpen && isMobile && <div className={styles.slideoutBackdrop} />}
        <div className={classNames(
          styles.slideoutWrapper, 
          isOpen ? styles.slideoutWrapper__show : styles.slideoutWrapper__collapsed
        )}>
          <div className={styles.slideout}>
            <div className={styles.slideout__header}>
              <div className={styles.slideout__title}>AI Assistant</div>
              <div className={styles.slideout__close} onClick={onClose}>
                <CloseIcon />
              </div>
            </div>
            <div className={styles.slideout__content}>
              <div className={styles.upgradePrompt}>
                <LockIcon />
                <h4>Premium Feature</h4>
                <p>Upgrade to access AI-powered document Q&A!</p>
                <A 
                  href="/account" 
                  hrefParams={{}}
                  className={styles.upgradeButton}
                >
                  Upgrade now
                </A>
              </div>
            </div>
          </div>
        </div>
      </>,
      document.body
    );
  }

  // Both scopes show a picker next; the chat view opens once a target is chosen.
  const handleScopeSelect = (scope: ChatScope) => {
    setChatScope(scope);
  };

  const handleDocumentSelect = (documentId: string, documentName: string, _contentType?: string) => {
    setSelectedDocumentId(documentId);
    setSelectedDocumentName(documentName);
    // contentType is available if needed in the future
    setCurrentView('chat');

    // Set initial assistant message
    setMessages([{
      id: `initial-${Date.now()}`,
      content: `Ask me anything about "${documentName}"`,
      role: 'assistant',
      timestamp: new Date()
    }]);
    setInput('');
  };

  const handleDealSelect = (dealId: string, dealName: string) => {
    setSelectedDealId(dealId);
    setSelectedDealName(dealName);
    setCurrentView('chat');

    // Set initial assistant message for deal scope
    setMessages([{
      id: `initial-${Date.now()}`,
      content: `I'll help you analyze all documents in "${dealName}". What would you like to know?`,
      role: 'assistant',
      timestamp: new Date()
    }]);
    setInput('');
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    
    const userMessage: Message = {
      id: Date.now().toString(),
      content: input.trim(),
      role: 'user',
      timestamp: new Date()
    };
    
    // Add user message
    setMessages(prev => [...prev, userMessage]);
    setInput('');

    // Add processing message for all requests
    const processingMessage: Message = {
      id: `processing-${Date.now()}`,
      content: "Let me look into that. This will only take a moment...",
      role: 'assistant',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, processingMessage]);

    setIsLoading(true);
    
    try {
      // Build variables based on current scope
      interface AskDocumentVariables {
        question: string;
        sessionToken?: string;
        includeAudio?: boolean;
        voiceName?: string;
        speakingRate?: number;
        scope?: string;
        documentId?: string;
        dealId?: string;
      }

      const variables: AskDocumentVariables = {
        question: userMessage.content,
        sessionToken: sessionToken,
        includeAudio: isMobile && autoPlayEnabled,
        voiceName: 'en-US-Chirp3-HD-Orus', // High-quality Chirp3 HD voice
        speakingRate: 1.0
      };

      // Add scope-specific parameters
      if (chatScope === 'document') {
        variables.scope = 'document';
        variables.documentId = selectedDocumentId;
      } else if (chatScope === 'deal') {
        variables.scope = 'deal';
        variables.dealId = selectedDealId;
      } else {
        // Default to document scope for backward compatibility
        variables.scope = 'document';
        variables.documentId = selectedDocumentId || documentId;
      }

      // Call the GraphQL mutation
      const { data } = await askDocument({
        variables
      });
      
      if (data?.askDocument) {
        // Store session token for future messages
        if (data.askDocument.sessionToken && !sessionToken) {
          setSessionToken(data.askDocument.sessionToken);
        }

        // Replace processing message with actual response
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: data.askDocument.answer,
          role: 'assistant',
          timestamp: new Date(),
          sources: data.askDocument.sources
        };

        // Remove the processing message and add the real response
        setMessages(prev => {
          // Filter out the processing message (it's the last assistant message)
          const filtered = prev.filter(msg => !msg.id.startsWith('processing-'));
          return [...filtered, assistantMessage];
        });

        // Refetch sessions so history cards reflect updated messageCount and lastMessage
        refetchSessions();

        // Play audio response if available and auto-play is enabled
        if (data.askDocument.audioResponse && autoPlayEnabled && isMobile) {
          playAudio(data.askDocument.audioResponse);
        }
      }
    } catch (error) {
      // Error is handled by onError callback in mutation
      // Additional error handling can be added here if needed
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = () => {
    // Clear current chat state and reset to scope selection
    seededDocumentRef.current = undefined;
    setSelectedDocumentId(undefined);
    setSelectedDocumentName(undefined);
    setSelectedDealId(undefined);
    setSelectedDealName(undefined);
    setChatScope(null);
    setMessages([]);
    setInput('');
    setSessionToken(null);
    setCurrentView('chat');
  };
  
  const handleDeleteSession = async (sessionId: string) => {
    setIsDeletingSession(true);
    await deleteChatSession({
      variables: { sessionId }
    });
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'history':
        const sessions = sessionsData?.myChatSessions || [];
        return (
          <ChatHistory
            sessions={sessions.map((s) => ({
              id: s.id,
              title: s.title || 'Untitled Chat',
              documentId: s.document?.id,
              documentName: s.documentName,
              dealId: s.deal?.id,
              dealName: s.deal?.title,
              scope: s.scope,
              lastMessage: s.lastMessage,
              messageCount: s.messageCount,
              createdAt: new Date(s.createdAt),
              updatedAt: new Date(s.updatedAt),
              sessionToken: s.sessionToken,
            }))}
            onNewChat={() => {
              // Clear all state and go to scope selection
              setSelectedDocumentId(undefined);
              setSelectedDocumentName(undefined);
              setSelectedDealId(undefined);
              setSelectedDealName(undefined);
              setChatScope(null);
              setMessages([]);
              setSessionToken(null);
              setCurrentView('chat');
            }}
            onSelectSession={async (session) => {
              // Load the full session data
              try {
                const { data } = await client.query({
                  query: GET_CHAT_SESSION_BY_TOKEN,
                  variables: { sessionToken: session.sessionToken },
                  fetchPolicy: 'network-only',
                });

                if (data?.chatSessionByToken) {
                  const fullSession = data.chatSessionByToken;
                  setSessionToken(fullSession.sessionToken);

                  // Restore scope and context
                  const sessionScope = fullSession.scope || 'document';
                  setChatScope(sessionScope as ChatScope);

                  if (sessionScope === 'document') {
                    setSelectedDocumentId(fullSession.document?.id);
                    setSelectedDocumentName(fullSession.documentName || fullSession.document?.title);
                  } else if (sessionScope === 'deal') {
                    setSelectedDealId(fullSession.deal?.id);
                    setSelectedDealName(fullSession.deal?.title);
                  }

                  // Convert messages to our format
                  const loadedMessages = fullSession.messages.map((m, index: number) => ({
                    id: `${m.timestamp}-${index}`, // Add index to ensure unique keys
                    content: m.content,
                    role: m.role,
                    timestamp: new Date(m.timestamp),
                    sources: m.metadata?.sources,
                  }));

                  setMessages(loadedMessages);
                  setCurrentView('chat');
                }
              } catch (error) {
                // Handle error loading session
                notifyError({ message: 'Failed to load chat session' });
              }
            }}
            onDeleteSession={handleDeleteSession}
            isDeletingSession={isDeletingSession}
          />
        );
      case 'chat':
        // Show scope selector if no scope is selected and no pre-selected document
        if (!chatScope && !documentId) {
          return (
            <ChatScopeSelector onScopeSelect={handleScopeSelect} />
          );
        }

        // Show document picker if document scope is selected but no document is selected
        if (chatScope === 'document' && !selectedDocumentId) {
          return (
            <DocumentPicker
              onDocumentSelect={handleDocumentSelect}
              onBack={() => setChatScope(null)}
              teamId={teamId}
            />
          );
        }

        // Show deal picker if deal scope is selected but no deal is selected
        if (chatScope === 'deal' && !selectedDealId) {
          return (
            <DealPicker
              onDealSelect={handleDealSelect}
              onBack={() => setChatScope(null)}
              teamId={teamId}
              defaultDealId={pageDealId}
            />
          );
        }

        return (
          <ChatView
            messages={messages}
            input={input}
            isLoading={isLoading}
            selectedDocumentName={selectedDocumentName || selectedDealName || null}
            onInputChange={setInput}
            onSendMessage={handleSendMessage}
            onDocumentSelect={handleDocumentSelect}
            onBack={() => {
              // If in a scope, go back to scope selection
              if (chatScope) {
                setChatScope(null);
                setSelectedDocumentId(undefined);
                setSelectedDocumentName(undefined);
                setSelectedDealId(undefined);
                setSelectedDealName(undefined);
                setMessages([]);
              } else {
                setCurrentView('history');
              }
            }}
            scrollRef={scrollRef}
            currentDealId={dealId}
          />
        );
    }
  };

  const getTitle = () => {
    switch (currentView) {
      case 'history':
        return 'AI Chat History';
      case 'chat':
        return 'AI Assistant';
    }
  };

  // Use React Portal to render slideout outside current DOM tree
  return ReactDOM.createPortal(
    <>
      {/* Dark overlay backdrop - only show on mobile */}
      {isOpen && isMobile && <div className={styles.slideoutBackdrop} />}
      <div className={classNames(
        styles.slideoutWrapper, 
        isOpen ? styles.slideoutWrapper__show : styles.slideoutWrapper__collapsed
      )}>
        <div className={styles.slideout}>
          {/* Sticky Header */}
          <div className={styles.slideout__header}>
            <div className={styles.slideout__title}>
              {currentView === 'history' && (
                <button 
                  className={styles.slideout__backButton}
                  onClick={() => setCurrentView('chat')}
                  title="Back to Chat"
                  aria-label="Back to Chat"
                >
                  <BackIcon />
                </button>
              )}
              <span>{getTitle()}</span>
              {currentView === 'chat' && (
                <>
                  <button
                    id="ai-assistant-info-btn"
                    className={styles.slideout__infoButton}
                    onClick={() => setShowTooltip(!showTooltip)}
                    aria-label="AI disclaimer information"
                  >
                    !
                  </button>
                  <ReactTooltip
                    anchorSelect="#ai-assistant-info-btn"
                    place="bottom"
                    isOpen={showTooltip}
                    clickable
                    border="1px solid #e0e0e0"
                    style={{
                      backgroundColor: 'rgba(221, 235, 239, 1)',
                      color: '#18191b',
                      maxWidth: '300px',
                      fontSize: '14px',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      zIndex: 10000,
                    }}
                  >
                    Information generated by AI is not legal or financial advice, and may contain inaccuracies. Please verify any important information.
                  </ReactTooltip>
                </>
              )}
            </div>
            {currentView === 'chat' && (
              <>
                {/* Auto-play toggle for mobile only */}
                {isMobile && (
                  <button
                    className={classNames(styles.slideout__autoPlayButton, {
                      [styles.active]: autoPlayEnabled
                    })}
                    onClick={() => {
                      const newValue = !autoPlayEnabled;
                      setAutoPlayEnabled(newValue);
                      localStorage.setItem('ai-chat-autoplay', newValue.toString());
                      
                      // Stop any currently playing audio if disabling
                      if (!newValue && isPlaying) {
                        stopAudio();
                      }
                    }}
                    title={autoPlayEnabled ? 'Auto-play ON' : 'Auto-play OFF'}
                    aria-label={`Toggle auto-play (currently ${autoPlayEnabled ? 'on' : 'off'})`}
                  >
                    {autoPlayEnabled ? <VolumeIcon /> : <VolumeOffIcon />}
                  </button>
                )}
                <button 
                  className={styles.slideout__newChatButton}
                  onClick={handleNewChat}
                  title="Start New Chat"
                  aria-label="Start New Chat"
                >
                  <PlusIcon />
                </button>
                <button 
                  className={styles.slideout__historyButton}
                  onClick={() => setCurrentView('history')}
                  title="View Chat History"
                  aria-label="View Chat History"
                >
                  <HistoryIcon />
                </button>
              </>
            )}
            <div className={styles.slideout__close} onClick={onClose}>
              <CloseIcon />
            </div>
          </div>

          {/* Scrollable Content */}
          <div className={styles.slideout__content}>
            {renderCurrentView()}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};